// @ts-strict-ignore
import { FloatRangeVO } from '@project/components/article/model/vo/property.vo';
import { Dictionary } from 'typescript-collections';
import { TextSizeCheckUtils } from '@shared/util/text-size-check-utils';
import { TextSizeCheckRequestResponseVO, TextSizeCheckRequestVO, TextSizeCheckVO } from '@3d-scene/manager/text-size-check.vo';
import { NumberUtils } from '@shared/util/number-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleVO } from '@article/model/vo/article.vo';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { TextCheckSpacingData } from '@3d-scene/manager/text-check-spacing-data';
import { InitManager } from '@shared/manager/init.manager';
import { Connection } from '@3d-scene/model3d/connection';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { TextureAreaVO } from '@shared/vo/meta-element.vo';
import { TextSizeCheckService } from '@3d-scene/service/text-size-check.service';
import { Injectable } from '@angular/core';
import { Log } from '@shared/debug/log';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TextSizeCheckManager {
  private _textSizeCheckInfos: Dictionary<string, TextSizeCheckVO[]> = new Dictionary<string, TextSizeCheckVO[]>();

  constructor(
    private textSizeCheckService: TextSizeCheckService,
    private labelingUtils: LabelingUtils,
  ) {}

  public getTextSizeCheck(
    textSizeCheckClass: string,
    spacing: number = NaN,
    spacingInRange: FloatRangeVO = null,
    updateByCommonRequest: boolean = false
  ): TextSizeCheckVO[] {
    if (!this._textSizeCheckInfos) {
      return null;
    }
    let id: string = TextSizeCheckUtils.getTextCheckIDFromParameters(textSizeCheckClass, spacing, spacingInRange);
    if (!this._textSizeCheckInfos.containsKey(id) || this._textSizeCheckInfos.getValue(id) === null) {
      if (updateByCommonRequest) {
        const commonId: string = TextSizeCheckUtils.getTextCheckIDFromParameters(textSizeCheckClass, NaN, null);
        const commonInfos: TextSizeCheckVO[] = this._textSizeCheckInfos.getValue(commonId);
        if (!commonInfos) {
          return null;
        }
        this._textSizeCheckInfos.setValue(id, this.getFilteredTextSizeChecksInfosBySpacing(commonInfos, spacing, spacingInRange));
      } else {
        id = TextSizeCheckUtils.getTextCheckIDFromParameters(textSizeCheckClass, NaN, null);
        if (!this._textSizeCheckInfos.containsKey(id)) {
          return null;
        }
      }
    }
    return this._textSizeCheckInfos.getValue(id);
  }

  public buildCommonTextSizeCheckRequest(connectionCubes: Connection[]): TextSizeCheckRequestVO[] {
    if (!connectionCubes) {
      return [];
    }
    const textSizeCheckRequests: TextSizeCheckRequestVO[] = [];
    connectionCubes.forEach((cube: Connection) => {
      const textSizeCheckClass: string = PropertyUtils.getStringPropertyValue(
        cube.metaElementVO.behaviorProperties,
        MetaElementBehaviorPropertyDef.TEXT_CHECK_CLASS
      );
      const rq: TextSizeCheckRequestVO = new TextSizeCheckRequestVO();
      rq.textSizeCheckClass = textSizeCheckClass;
      if (this.isRetrievalNecessary(rq, textSizeCheckRequests)) {
        textSizeCheckRequests.push(rq);
      }
    });
    return textSizeCheckRequests;
  }

  public buildTextSizeCheckRequest(connectionCubes: Connection[]): TextSizeCheckRequestVO[] {
    if (!connectionCubes) {
      return [];
    }
    const textSizeCheckRequests: TextSizeCheckRequestVO[] = [];
    connectionCubes.forEach((cube: Connection) => {
      if (cube.metaElementVO instanceof TextureAreaVO) {
        const spacingData: TextCheckSpacingData = this.labelingUtils.getTextCheckSpacingDataByTextureCube(cube);
        const textSizeCheckClass: string = PropertyUtils.getStringPropertyValue(
          cube.metaElementVO.behaviorProperties,
          MetaElementBehaviorPropertyDef.TEXT_CHECK_CLASS
        );
        const req: TextSizeCheckRequestVO = this.getTextSizeCheckRequest(textSizeCheckClass, spacingData);
        if (req) {
          textSizeCheckRequests.push(req);
        }
      }
    });
    return textSizeCheckRequests;
  }

  private getFilteredTextSizeChecksInfosBySpacing(infos: TextSizeCheckVO[], spacing: number, spacingInRange: FloatRangeVO): TextSizeCheckVO[] {
    if (!infos) {
      return null;
    }
    let filteredInfos: TextSizeCheckVO[];
    if (NumberUtils.isNumber(spacing)) {
      filteredInfos = infos.filter((textCheckVo) => MathUtils.compare(spacing, textCheckVo.spacing) === 0);
    } else if (spacingInRange && MathUtils.compare(spacingInRange.left, spacingInRange.right) !== 0) {
      filteredInfos = infos.filter(
        (textCheckVo) =>
          MathUtils.compare(spacingInRange.left, textCheckVo.spacing) <= 0 && MathUtils.compare(spacingInRange.right, textCheckVo.spacing) >= 0
      );
    } else {
      filteredInfos = infos;
    }
    return filteredInfos;
  }

  public async retrieveTextSizeCheckInfos(textSizeCheckRequests: TextSizeCheckRequestVO[]): Promise<boolean> {
    if (ArrayUtils.isEmpty(textSizeCheckRequests)) {
      return true;
    }
    const processedTextCheckIds: Set<string> = new Set<string>();
    const unfetchedRequests: TextSizeCheckRequestVO[] = textSizeCheckRequests.filter((textSizeCheckRequestVO) => {
      const textCheckIDFromRequest: string = TextSizeCheckUtils.getTextCheckIDFromRequest(textSizeCheckRequestVO);
      if (processedTextCheckIds.has(textCheckIDFromRequest)) {
        return false;
      }
      processedTextCheckIds.add(textCheckIDFromRequest);
      return !this.existTextCheckId(textCheckIDFromRequest);
    });
    return this.startRetrievalForTextSizeCheckClasses(unfetchedRequests);
  }

  public isRetrievalNecessary(textSizeCheckRequestVO: TextSizeCheckRequestVO, currentRequestList: TextSizeCheckRequestVO[] = null): boolean {
    const textCheckIDFromRequest: string = TextSizeCheckUtils.getTextCheckIDFromRequest(textSizeCheckRequestVO);
    if (this.existTextCheckId(textCheckIDFromRequest)) {
      return false;
    }
    if (!ArrayUtils.isEmpty(currentRequestList)) {
      for (const request of currentRequestList) {
        if (textCheckIDFromRequest === TextSizeCheckUtils.getTextCheckIDFromRequest(request)) {
          return false;
        }
      }
    }
    return true;
  }

  public getTextSizeCheckWithFallback(textSizeCheckClass: string, spacingData: TextCheckSpacingData, isDynamicLabelingArticle: boolean): TextSizeCheckVO[] {
    if (!spacingData || (NumberUtils.isNotNumber(spacingData.spacing) && !spacingData.spacingInRange)) {
      return this.getTextSizeCheck(textSizeCheckClass);
    }

    if (isDynamicLabelingArticle) {
      return this.getTextSizeChecksForLabelStrip(textSizeCheckClass, spacingData);
    } else {
      return this.getTextSizeCheck(textSizeCheckClass, spacingData.spacing, spacingData.spacingInRange);
    }
  }

  public getTextSizeCheckWithFallbackByConnection(connection: Connection): TextSizeCheckVO[] {
    if (!connection) {
      return [];
    }
    const resolvedTextSizeCheckClass: string = PropertyUtils.getStringPropertyValue(
      connection.metaElementVO?.behaviorProperties,
      MetaElementBehaviorPropertyDef.TEXT_CHECK_CLASS,
    );
    if (!resolvedTextSizeCheckClass) {
      return [];
    }
    const spacingData: TextCheckSpacingData = this.labelingUtils.getTextCheckSpacingDataByTextureCube(connection);
    return this.getTextSizeCheckWithFallback(
      resolvedTextSizeCheckClass,
      spacingData,
      ArticleUtils.isDynamicLabelingArticle(connection.article),
    ) ?? [];
  }

  private getTextSizeChecksForLabelStrip(textSizeCheckClass: string, spacingData: TextCheckSpacingData) {
    // use text check from common request and filter it by given spacing in case of label-strip
    let textSizeChecks: TextSizeCheckVO[] = this.getTextSizeCheck(textSizeCheckClass, spacingData.spacing, spacingData.spacingInRange, true);
    if (ArrayUtils.isEmpty(textSizeChecks)) {
      textSizeChecks = this.getTextSizeCheckWithDecidingSpacing(textSizeCheckClass, spacingData.getAverageSpacing());
      if (ArrayUtils.isNotEmpty(textSizeChecks)) {
        this._textSizeCheckInfos.setValue(
          TextSizeCheckUtils.getTextCheckIDFromParameters(textSizeCheckClass, spacingData.spacing, spacingData.spacingInRange),
          textSizeChecks
        );
      }
    }
    return textSizeChecks;
  }

  public getTextSizeCheckWithDecidingSpacing(textSizeCheckClass: string, averageSpacing: number): TextSizeCheckVO[] {
    const textChecks: TextSizeCheckVO[] = this.getTextSizeCheck(textSizeCheckClass);
    if (ArrayUtils.isEmpty(textChecks)) {
      return [];
    }
    let nearestGreaterSpacing: number = NaN;
    let nearestSmallerSpacing: number = NaN;
    for (const textCheck of textChecks) {
      if (
        MathUtils.compare(textCheck.spacing, averageSpacing) > -1 &&
        (NumberUtils.isNotNumber(nearestGreaterSpacing) || MathUtils.compare(textCheck.spacing, nearestGreaterSpacing) < 0)
      ) {
        nearestGreaterSpacing = textCheck.spacing;
      } else if (
        MathUtils.compare(textCheck.spacing, averageSpacing) < 1 &&
        (NumberUtils.isNotNumber(nearestSmallerSpacing) || MathUtils.compare(textCheck.spacing, nearestSmallerSpacing) > 0)
      ) {
        nearestSmallerSpacing = textCheck.spacing;
      } else if (MathUtils.compare(nearestGreaterSpacing, nearestSmallerSpacing) === 0) {
        // averageSpacing already exists in retrieved textChecks
        break;
      }
    }
    let calculatedSpacing: number;
    if (NumberUtils.isNotNumber(nearestGreaterSpacing)) {
      calculatedSpacing = nearestSmallerSpacing;
    } else if (NumberUtils.isNotNumber(nearestSmallerSpacing)) {
      calculatedSpacing = nearestGreaterSpacing;
    } else {
      const calculatedSpacingDecidingValue: number =
        nearestSmallerSpacing + (nearestGreaterSpacing - nearestSmallerSpacing) * InitManager.spacingDecidingValue;
      if (averageSpacing > calculatedSpacingDecidingValue) {
        calculatedSpacing = nearestGreaterSpacing;
      } else {
        calculatedSpacing = nearestSmallerSpacing;
      }
    }
    return this.getTextSizeCheck(textSizeCheckClass, calculatedSpacing, null, true);
  }

  public getTextSizeCheckRequestsByTerminalArticleAndTextCheckClasses(article: ArticleVO, textCheckClasses: string[]): TextSizeCheckRequestVO[] {
    if (ArrayUtils.isEmpty(textCheckClasses)) {
      return [];
    }
    const requests: TextSizeCheckRequestVO[] = [];
    textCheckClasses.forEach((textCheckClass: string) => {
      let req: TextSizeCheckRequestVO = null;
      if (article) {
        const spacingData: TextCheckSpacingData = LabelingUtils.getTextCheckSpacingDataByTerminalArticle(article);
        if (spacingData && (NumberUtils.isNumber(spacingData.spacing) || spacingData.spacingInRange)) {
          req = this.getTextSizeCheckRequest(textCheckClass, spacingData);
        }
      }
      if (req) {
        requests.push(req);
      }
    });
    return requests;
  }

  public getTextSizeCheckRequest(textSizeCheckClass: string, spacingData: TextCheckSpacingData): TextSizeCheckRequestVO {
    if (!textSizeCheckClass || !spacingData) {
      return null;
    }
    const req: TextSizeCheckRequestVO = new TextSizeCheckRequestVO();
    req.textSizeCheckClass = textSizeCheckClass;
    req.spacing = spacingData.spacing;
    req.spacingInRange = spacingData.spacingInRange;
    return this.isRetrievalNecessary(req) ? req : null;
  }

  private existTextCheckId(textCheckIDFromRequest: string): boolean {
    return this._textSizeCheckInfos.getValue(textCheckIDFromRequest) !== undefined;
  }

  private async startRetrievalForTextSizeCheckClasses(textSizeCheckRequests: TextSizeCheckRequestVO[]): Promise<boolean> {
    if (!ArrayUtils.isEmpty(textSizeCheckRequests)) {

      try {
        const textSizeCheckRequestResponseVOs: TextSizeCheckRequestResponseVO[] = await
          firstValueFrom(this.textSizeCheckService.getTextSizeCheckRequestResponse(textSizeCheckRequests));
        return this.textSizeCheckPreloadSuccessHandler(textSizeCheckRequestResponseVOs);
      } catch (error) {
        Log.error('Could not get text size: ', error)
        return false;
      }
    } else {
      return true;
    }
  }

  private async textSizeCheckPreloadSuccessHandler(textSizeCheckRequestResponseVOs: TextSizeCheckRequestResponseVO[]): Promise<boolean> {
    for (let ro of textSizeCheckRequestResponseVOs) {
      const requestId: string = TextSizeCheckUtils.getTextCheckIDFromRequest(ro.request);
      let textSizeCheckVOs: TextSizeCheckVO[] = this._textSizeCheckInfos.getValue(requestId);
      if (!textSizeCheckVOs) {
        textSizeCheckVOs = [];
      }
      if (!ArrayUtils.isEmpty(ro.response)) {
        this._textSizeCheckInfos.setValue(requestId, textSizeCheckVOs.concat(ro.response));
      } else if ((NumberUtils.isNumber(ro.request.spacing) && ro.request.spacing !== 0) || ro.request.spacingInRange) {
        this._textSizeCheckInfos.setValue(requestId, null);
        const rq: TextSizeCheckRequestVO = new TextSizeCheckRequestVO();
        rq.textSizeCheckClass = ro.request.textSizeCheckClass;
        if (this.isRetrievalNecessary(rq)) {
          // only trigger to retrieve text size check if retrieval is necessary,
          // otherwise this trigger will cause sending success message, even in cases where still a backlog exists.
          // So without this explicit check here for isRetrievalNecessary, success message(s) will be send out early and multiple times.
          // This is not desired, and causes incorrect behaviour during import old pro serve projects.
          await this.retrieveTextSizeCheckInfos([rq]);
        }
      } else {
        this._textSizeCheckInfos.setValue(requestId, textSizeCheckVOs);
      }
    }
    return true;
  }
}
